import { Lock, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getCurrentCustomer } from '../../lib/auth';

export interface LockedContentBlockProps {
  props: {
    title?: string | null;
    contentPreview?: string | null;
  };
}

/**
 * Rendered whenever a CMS block (or entire page) is gated behind the loyalty
 * program. Visitors who are not logged in see a login/signup CTA; logged-in
 * customers who are not loyalty members see an "enrol" message.
 */
export function LockedContentBlock({ props }: LockedContentBlockProps) {
  const navigate = useNavigate();
  const currentCustomer = getCurrentCustomer();
  const isLoggedIn = !!currentCustomer;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-zinc-200 bg-white shadow-sm">
      {/* Blurred preview */}
      {props.contentPreview && (
        <div
          className="px-6 pt-6 pb-16 text-sm text-zinc-600 leading-relaxed select-none"
          style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none' }}
          aria-hidden="true"
        >
          {props.contentPreview}
          <span className="opacity-50"> …</span>
        </div>
      )}

      {/* Lock overlay */}
      <div
        className={`${props.contentPreview ? 'absolute inset-0' : 'py-12'} flex flex-col items-center justify-center gap-4 px-6 text-center`}
        style={
          props.contentPreview
            ? {
                background:
                  'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.85) 35%, white 65%)',
              }
            : undefined
        }
      >
        <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-100 flex items-center justify-center">
          <Lock size={22} className="text-amber-500" />
        </div>

        <div className="space-y-1 max-w-xs">
          {props.title && <p className="font-semibold text-zinc-900">{props.title}</p>}
          <p className="text-sm text-zinc-500">
            {isLoggedIn
              ? 'This content is exclusive to loyalty program members.'
              : 'This content is exclusive to loyalty program members. Sign in or create an account to access it.'}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          {isLoggedIn ? (
            <button
              onClick={() => navigate('/loyalty')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[--color-primary] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <Star size={14} />
              Join the loyalty program
            </button>
          ) : (
            <>
              <button
                onClick={() => navigate('/signup')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[--color-primary] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Create free account
              </button>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-zinc-200 text-zinc-700 text-sm font-semibold hover:border-zinc-300 transition-colors"
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
